/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import omit from 'lodash/omit';
import { configureStore, combineReducers } from '@reduxjs/toolkit';

import { store as sarsaparillaStore, sarsaparillaReducers } from 'sarsaparilla';
import { authReducers } from 'shared-ui/auth';

import { externalRatingReviewsReducers } from 'ui-rating-review/src/reducers';
import searchResultsReducers from '../src/reducers/searchResults';
import searchAggregationsReducers from '../src/reducers/searchAggregations';
import tripPreferencesReducers from '../src/reducers/tripPreferences';
import searchMapSyncReducers from '../src/reducers/searchMapSyncReducer';
import searchCriteriaReducers from '../src/reducers/searchCriteriaReducers';
import searchSuggestionsReducers from '../src/reducers/searchSuggestionsReducers';

import searchStore from '../src/store';

const initialState = {
    ...sarsaparillaStore(),
    ...searchStore(),
};

const searchReducers = {
    searchSuggestions: searchSuggestionsReducers,
    searchResults: searchResultsReducers,
    searchAggregations: searchAggregationsReducers,
    searchMapSync: searchMapSyncReducers,
    search: searchCriteriaReducers,
};

const rootReducer = combineReducers({
    tripPreferences: tripPreferencesReducers,
    ratingReviews: externalRatingReviewsReducers,
    ...omit(sarsaparillaReducers, ['search', 'searchSuggestions']),
    ...searchReducers,

    // Auth - MUST COME AFTER SARSA REDUCERS DURING AUTH MIGRATION
    ...authReducers,
});

export function setupStore(preloadedState?: any) {
    const store = configureStore({
        preloadedState,
        reducer: rootReducer,
        middleware: (getDefaultMiddleware) => {
            return getDefaultMiddleware({
                serializableCheck: {
                    ignoredActions: ['launchdarkly/setClient'],
                    ignoredPaths: ['launchdarkly'],
                },
            });
        },
        devTools: process.env.DEV_TOOLS_ENABLED,
    });

    return store;
}

const store = setupStore(initialState);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppStore = typeof store;

export default store;
